import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Typography,
  MenuItem,
  makeStyles,
} from "@material-ui/core";

import { FleetSettingsContext, RiderContext } from "../../../context";
import { useTestIds } from "../../../hooks/useTestIds";
import { withSnackbar } from "../../../HOC/withSnackbar";
import { FilledSelect } from "../../../components/FormElements";

const useStyles = makeStyles(() => ({
  formControl: {
    width: "100%",
  },
  actions: {
    padding: "1rem",
  },
  warning: {
    padding: "1rem 0",
  },
}));

const IssueFine = ({ setSnackbarMessage, setAlertSeverity }) => {
  const { activeTrip, issueFine, activeReservation } = useContext(RiderContext);
  const { activeRatePlan } = useContext(FleetSettingsContext);
  const classes = useStyles();
  const history = useHistory();
  const testIds = useTestIds("rider-profile-issue-fine-modal");
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);
  const [selectedFine, setSelectedFine] = useState("");
  const [fines, setFines] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleClose = (e = null) => {
    if (e) e.stopPropagation();
    setIsOpen(false);
    history.goBack();
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const finePayload = {
        rate_plan_fine_type_id: selectedFine,
        reservation_id: activeReservation?.id,
      };
      await issueFine(activeTrip.id, finePayload);
      setSnackbarMessage(t("Riders.FineModal.SuccessMessage"));
      setAlertSeverity("success");
    } catch (e) {
      setSnackbarMessage(t("Riders.FineModal.FailureMessage"));
      setAlertSeverity("error");
    }
    handleClose();
    setLoading(false);
  };

  const changeSelectedFine = (event) => {
    setSelectedFine(event.target.value);
  };

  useEffect(() => {
    if (!fines.length) {
      setFines(activeRatePlan?.rate_plan_fine_types || []);
    }
  }, [activeRatePlan?.rate_plan_fine_types]);

  return (
    <Dialog
      data-testid={testIds.wrapper}
      open={isOpen}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle data-testid={testIds.title}>{t("Riders.FineModal.IssueFine")}</DialogTitle>
      <DialogContent data-testid={testIds.content}>
        <Grid container justify="space-between">
          <Grid item xs={12}>
            <FormControl variant="filled" className={classes.formControl} margin="normal">
              <FilledSelect
                id="issueFine"
                label={t("Riders.FineModal.SelectFine")}
                value={selectedFine}
                onChange={changeSelectedFine}
                SelectProps={{
                  disabled: !fines?.length,
                }}
                testIds={{
                  label: testIds.selectLabel,
                  select: testIds.selectInput,
                }}
              >
                {fines?.map((fine, idx) => (
                  <MenuItem key={fine.id} value={fine.id} data-testid={`${testIds.option}-${idx}`}>
                    {fine.name}
                  </MenuItem>
                ))}
              </FilledSelect>
            </FormControl>
          </Grid>
          <Grid item xs={12} className={classes.warning}>
            <Typography data-testid={testIds.warning}>
              {t("Riders.FineModal.IssueFineWarning")}
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button data-testid={testIds.cancelButton} onClick={handleClose}>
          {t("Riders.FineModal.Cancel")}
        </Button>
        <Button
          data-testid={testIds.submitButton}
          onClick={handleSubmit}
          disabled={!selectedFine.length || loading}
        >
          {t("Riders.FineModal.IssueFine")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default withSnackbar(IssueFine);
IssueFine.propTypes = {
  setSnackbarMessage: PropTypes.func.isRequired,
  setAlertSeverity: PropTypes.func,
};

IssueFine.defaultProps = {
  setAlertSeverity: () => {},
};
